import { useRef } from 'react';

import { isBrowser as _isBrowser } from '../utils/isBrowser.js';

/**
 * Array tuple featuring the throttled function instance and its cancellation handler
 * @internal
 */
export type useThrottleReturnType<T extends unknown[]> = [
  /** Throttled instance of the function passed through the hook payload */
  (...args: T) => void,
  /** Cancellation handler, which will terminate any pending throttled function execution */
  () => void,
];

/**
 * Simplistic hook designed for throttling execution of pure functions based on the `window.requestAnimationFrame` API.
 * @returns Tuple array containing the throttled function instance and a cancel handler
 * @internal
 */
export function useRequestAnimationFrame<T extends unknown[]>(
  /** Function to be throttled. */
  fn: (...args: T) => void,
): useThrottleReturnType<T> {
  const requestAfIdRef = useRef<number | null>(null);

  const throttledFn = (...args: T) => {
    if (requestAfIdRef.current === null && _isBrowser) {
      requestAfIdRef.current = window.requestAnimationFrame(() => {
        fn.call(fn, ...args);
        requestAfIdRef.current = null;
      });
    }
  };

  const cancelThrottledFn = () => {
    if (requestAfIdRef.current !== null && _isBrowser) {
      window.cancelAnimationFrame(requestAfIdRef.current);
      requestAfIdRef.current = null;
    }
  };

  return [throttledFn, cancelThrottledFn];
}
